import { createContext, useContext, ReactNode, useState, useMemo, useCallback } from "react";

type SaveFn = () => void;

interface LayoutSaveContextProps {
  saveFn: SaveFn | null;
  setSaveFn: (fn: SaveFn | null) => void;
  isSaving: boolean;
  setIsSaving: (saving: boolean) => void;
}

const LayoutSaveContext = createContext<LayoutSaveContextProps | undefined>(undefined);

interface LayoutSaveProviderProps {
  children: ReactNode;
}

const LayoutSaveProvider: React.FC<LayoutSaveProviderProps> = ({ children }) => {
  const [saveFn, setSaveFnState] = useState<SaveFn | null>(null);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  const setSaveFn = useCallback((fn: SaveFn | null) => {
    setSaveFnState(() => fn);
  }, []);

  const value = useMemo(
    () => ({ saveFn, setSaveFn, isSaving, setIsSaving }),
    [saveFn, setSaveFn, isSaving],
  );

  return <LayoutSaveContext.Provider value={value}>{children}</LayoutSaveContext.Provider>;
};

const useLayoutSave = () => {
  const context = useContext(LayoutSaveContext);

  if (!context) {
    throw new Error("useLayoutSave must be used within a LayoutSaveProvider");
  }

  return context;
};

export { LayoutSaveProvider, useLayoutSave };
